/**
 * Live refresh plumbing for the Clients (Client 360) area — a THIN SELECTOR
 * over the shared realtime bus (`src/realtime/`).
 *
 * The API's Knex services emit standard Feathers CRUD events
 * (`created` / `patched` / `updated` / `removed`) which channels.ts publishes to
 * the `internal` staff channel; the Socket.io transport delivers them as
 * `"<servicePath> <event>"`. `realtime/channels.ts`'s `clientCrudChannel` is the
 * single listener set for the four client-scoped services; ClientDetailStore
 * registers a handler scoped to the open clientId and patches its rows in place.
 *
 * This file used to open its OWN socket ("copied from the shared-socket
 * refcount pattern in views/devops/devopsLiveStatus.ts") and the Clients LIST
 * opened a THIRD one for a subset of the same events. Both now select from one
 * channel on one connection: a user on Client 360 no longer holds 4+ websockets.
 *
 * CONNECTION: none is opened here — see `realtime/realtimeBus.ts`.
 * AUTH: sourced internally by the bus from the AuthStore; no token parameter.
 * IF THE STREAM IS DOWN: the bus records a loud diagnostic and the view stays
 * pull-only (header Refresh / post-write re-pulls) — see
 * `realtime/freshnessPolicy.ts`.
 */
import type RootStore from "stores/RootStore";
import {
  CLIENT_REALTIME_SERVICES,
  clientCrudChannel,
  meetingIntelChannel,
  subscribeRealtime,
  type ClientRealtimeService,
  type CrudAction,
  type ServiceCrudEvent,
} from "realtime";

export { CLIENT_REALTIME_SERVICES };
export type { ClientRealtimeService };

/** Historical alias for the CRUD action union. */
export type ClientRealtimeAction = CrudAction;

/** One CRUD event off any of the four client-scoped services. */
export type ClientServiceEvent = ServiceCrudEvent<ClientRealtimeService>;

type ClientEventHandler = (event: ClientServiceEvent) => void;

/**
 * Subscribe to live client-area CRUD events (all four services). Returns an
 * idempotent disposer; the bus tears the listeners down when the last
 * subscriber across the app leaves (route onExit).
 *
 * @param rootStore used by the bus to resolve the shared connection and the
 *   AuthStore JWT. The caller cannot supply a token.
 */
export function subscribeClientEvents(
  rootStore: RootStore,
  handler: ClientEventHandler
): () => void {
  return subscribeRealtime(rootStore, clientCrudChannel, handler);
}

/** The pipeline's announcements a client's page follows, by what each says has been written. */
export type ClientMeetingEventKind =
  | "meeting-analyzed"
  | "meeting-processed"
  | "meeting-enriched"
  | "meeting-distributed"
  | "meeting-mapped";

/** A meeting of one client moved through the pipeline (the pipeline's own announcement). */
export interface ClientMeetingAnalysedEvent {
  kind: ClientMeetingEventKind;
  clientId: string;
  /** `meeting-mapped` only: the client the meeting belonged to before (a re-map), else null. */
  previousClientId: string | null;
  meetingId: number | null;
}

const CLIENT_MEETING_KINDS: ReadonlySet<string> = new Set<ClientMeetingEventKind>([
  "meeting-analyzed",
  "meeting-processed",
  "meeting-enriched",
  "meeting-distributed",
  "meeting-mapped",
]);

const idOrNull = (v: unknown): string | null => (v === undefined || v === null || String(v) === "" ? null : String(v));

/**
 * The meeting pipeline writes some of a client's rows straight to their
 * tables (the relationship ledger's signals, the meeting's mapping, the
 * assignments distribution creates), so those writes emit no CRUD event. What
 * it does announce, naming the client: `meeting-analyzed` (Gantry's result
 * push, the default authority — everything stored), `meeting-processed` (HQ's
 * own pass, the rollback — the items, before its enrichment),
 * `meeting-enriched` (that pass's signals and analyses written) and
 * `meeting-distributed` (a run routed the meeting's items, on any path) and
 * `meeting-mapped` (an operator mapped the meeting to the client, moving any
 * work it had with it; it names the client it left, too). This selects those,
 * for one client, out of the shared Meeting Intelligence channel — no second
 * listener set for the same wire events.
 */
export function subscribeClientMeetingEvents(
  rootStore: RootStore,
  handler: (event: ClientMeetingAnalysedEvent) => void
): () => void {
  return subscribeRealtime(rootStore, meetingIntelChannel, (event) => {
    if (!CLIENT_MEETING_KINDS.has(event.kind)) return;
    const clientId = event.payload.clientId;
    if (clientId === undefined || clientId === null || String(clientId) === "") return;
    const meetingId = Number(event.payload.meetingId);
    handler({
      kind: event.kind as ClientMeetingEventKind,
      clientId: String(clientId),
      previousClientId: idOrNull(event.payload.previousClientId),
      meetingId: Number.isFinite(meetingId) ? meetingId : null,
    });
  });
}

/** An assignment's work moved: accepted, rejected, completed, or set to another module. */
export interface ClientWorkEvent {
  assignmentId: string | null;
  /** The modules it names — a module move's new one and the one it left; empty for the other three. */
  moduleIds: string[];
  /** The clients it names — a retarget's new client and the one it left; empty otherwise. */
  clientIds: string[];
}

const WORK_KINDS = new Set([
  "assignment-accepted",
  "assignment-rejected",
  "assignment-completed",
  "assignment-module-changed",
]);

/**
 * The assignment notices, out of the shared Meeting Intelligence channel, for
 * a view that counts tracked work (Client 360's Deliverables "Tasks" chip,
 * #3820). The notices name an assignment and never its client, so the view
 * matches them against the assignments it counted; a module move (set-module,
 * a retarget, its undo) also names the modules involved, and a retarget that
 * took the work to another client names both clients. A rejection covers
 * every way work stops counting — an operator's reject, an item closed with
 * its work, a re-analysis removing an unaccepted assignment.
 */
export function subscribeClientWorkEvents(
  rootStore: RootStore,
  handler: (event: ClientWorkEvent) => void
): () => void {
  return subscribeRealtime(rootStore, meetingIntelChannel, (event) => {
    if (!WORK_KINDS.has(event.kind)) return;
    const { assignmentId, moduleId, previousModuleId, clientId, previousClientId } = event.payload;
    handler({
      assignmentId: idOrNull(assignmentId),
      moduleIds: [idOrNull(moduleId), idOrNull(previousModuleId)].filter((m): m is string => m !== null),
      clientIds: [idOrNull(clientId), idOrNull(previousClientId)].filter((c): c is string => c !== null),
    });
  });
}
